import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Building2, Truck, Zap,
  Pencil, Trash2, Loader2, Briefcase, ExternalLink 
} from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { Opportunity, OPPORTUNITY_STATUSES, useOpportunities } from '@/hooks/useOpportunities';
import { OpportunityModal } from '@/components/charging/OpportunityModal';
import { OpportunityNotes } from '@/components/charging/OpportunityNotes';
import { OpportunityTodos } from '@/components/charging/OpportunityTodos';
import { OpportunityAddressesSection } from '@/components/charging/OpportunityAddressesSection';
import { OpportunityLocationsSection } from '@/components/charging/OpportunityLocationsSection';
import { EntityQuotes } from '@/components/crm/quote/EntityQuotes';
import { EntityAppointments } from '@/components/crm/EntityAppointments';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function ChargingOpportunityDetail() {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateOpportunity, deleteOpportunity } = useOpportunities();
  
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Determine portal type from current route
  const isCrmPortal = location.pathname.startsWith('/crm');
  const backPath = isCrmPortal ? '/crm/charging/opportunities' : '/charging/opportunities';
  const contactPath = isCrmPortal ? '/crm/contacts' : '/charging/contacten';
  const orgPath = isCrmPortal ? '/crm/organisaties' : '/crm/organisaties';

  const fetchOpportunity = async () => {
    if (!opportunityId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('charging_opportunities')
        .select(`
          *,
          organization:organizations(id, name, website, favicon_url, logo_url, cached_favicon_url),
          contact:contacts(id, first_name, last_name, email, org_id),
          opportunity_contacts(
            id,
            contact_id,
            position,
            contact:contacts(id, first_name, last_name, email, org_id)
          )
        `)
        .eq('id', opportunityId)
        .is('deleted_at', null)
        .single();

      if (error) throw error;
      setOpportunity(data as Opportunity);
    } catch (error) {
      console.error('Error fetching opportunity:', error);
      navigate(backPath);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOpportunity();
  }, [opportunityId]);

  const handleStatusChange = async (newStatus: string) => {
    if (!opportunity) return;
    await updateOpportunity(opportunity.id, { status: newStatus });
    setOpportunity(prev => prev ? { ...prev, status: newStatus } : null);
  };

  const handleSave = async (data: Partial<Opportunity> & { contact_ids?: string[] }) => {
    if (!opportunity) return;
    await updateOpportunity(opportunity.id, data);
    await fetchOpportunity();
  };

  const handleDelete = async () => {
    if (!opportunity) return;
    await deleteOpportunity(opportunity.id);
    navigate(backPath);
  };

  const getOrgIcon = (org?: { logo_url?: string | null; favicon_url?: string | null; website?: string | null } | null) => {
    if (org?.logo_url || org?.favicon_url) {
      return (
        <img
          src={org.logo_url || org.favicon_url || ''}
          alt=""
          className="h-5 w-5 object-contain"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      );
    }
    if (org?.website) {
      const domain = org.website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      return (
        <img
          src={`https://www.google.com/s2/favicons?sz=32&domain=${domain}`}
          alt=""
          className="h-5 w-5 object-contain"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      );
    }
    return <Building2 className="h-5 w-5 text-muted-foreground" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Opportunity niet gevonden</p>
        <Button variant="outline" onClick={() => navigate(backPath)} className="mt-4">
          Terug naar overzicht
        </Button>
      </div>
    );
  }

  const status = OPPORTUNITY_STATUSES.find(s => s.id === opportunity.status);
  
  // Get all contacts
  const allContacts = opportunity.opportunity_contacts?.length
    ? opportunity.opportunity_contacts.sort((a, b) => a.position - b.position).map(oc => oc.contact)
    : opportunity.contact ? [opportunity.contact] : [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(backPath)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="h-6 w-6" />
            {opportunity.name}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setEditModalOpen(true)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => setDeleteDialogOpen(true)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {/* Main content: 3 columns on lg */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Details card - left column */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status */}
              <div>
                <label className="text-sm text-muted-foreground">Status</label>
                <Select value={opportunity.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OPPORTUNITY_STATUSES.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="h-2 w-2 rounded-full" 
                            style={{ backgroundColor: s.color }}
                          />
                          <span>{s.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Organization - clickable name */}
              {opportunity.organization && (
                <div>
                  <label className="text-sm text-muted-foreground">Organisatie</label>
                  <div className="flex items-center gap-2 mt-1">
                    {getOrgIcon(opportunity.organization)}
                    <Button
                      variant="link"
                      className="p-0 h-auto font-medium"
                      onClick={() => navigate(`${orgPath}/${opportunity.organization!.id}`)}
                    >
                      {opportunity.organization.name}
                    </Button>
                    {opportunity.organization.website && (
                      <a 
                        href={opportunity.organization.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Contacts */}
              {allContacts.length > 0 && (
                <div>
                  <label className="text-sm text-muted-foreground">
                    Contactpersonen ({allContacts.length})
                  </label>
                  <div className="space-y-2 mt-1">
                    {allContacts.map((contact, index) => {
                      if (!contact) return null;
                      const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
                      const contactOrg = contact.org_id 
                        ? { ...opportunity.organization } 
                        : null;
                      
                      return (
                        <div key={contact.id} className="flex items-start gap-2">
                          {getOrgIcon(contactOrg)}
                          <div>
                            <Button 
                              variant="link" 
                              className="p-0 h-auto"
                              onClick={() => navigate(`${contactPath}/${contact.id}`)}
                            >
                              {contactName || contact.email}
                            </Button>
                            {contact.email && contactName && (
                              <div className="text-sm text-muted-foreground">
                                {contact.email}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Fleet info */}
              {(opportunity.total_fleet || opportunity.electric_trucks) && (
                <div>
                  <label className="text-sm text-muted-foreground">Vloot informatie</label>
                  <div className="flex items-center gap-4 mt-1">
                    {opportunity.total_fleet && (
                      <div className="flex items-center gap-1">
                        <Truck className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{opportunity.total_fleet}</span>
                        <span className="text-sm text-muted-foreground">totaal</span>
                      </div>
                    )}
                    {opportunity.electric_trucks && (
                      <div className="flex items-center gap-1 text-green-600">
                        <Zap className="h-4 w-4" />
                        <span className="font-medium">{opportunity.electric_trucks}</span>
                        <span className="text-sm">e-trucks</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              {opportunity.description && (
                <div>
                  <label className="text-sm text-muted-foreground">Beschrijving</label>
                  <p className="mt-1 text-sm whitespace-pre-wrap">{opportunity.description}</p>
                </div>
              )}

              {/* Created at */}
              <div className="pt-4 border-t text-sm text-muted-foreground">
                Aangemaakt op {format(new Date(opportunity.created_at), "d MMMM yyyy 'om' HH:mm", { locale: nl })}
              </div>
            </CardContent>
          </Card>
          {/* Vervoerder locaties */}
          <div className="mt-4">
            <OpportunityAddressesSection 
              opportunityId={opportunity.id} 
              organizationName={opportunity.organization?.name}
            />
          </div>
        </div>

        {/* Right columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* To-do's */}
          <OpportunityTodos opportunityId={opportunity.id} />
          
          {/* Geïnteresseerde laadlocaties */}
          <OpportunityLocationsSection opportunityId={opportunity.id} />

          {/* T2.2 - sinds migratie 032 kan een offerte aan een Opportunity hangen.
              Dit is de scherpste koppeling van de traject-as: het aanbod gaat naar
              deze vloot. De kavels waar die vloot op wil laden staan hierboven. */}
          <EntityQuotes scope="opportunity" entityId={opportunity.id} />

          {/* T2.3 - afspraken met deze vloot. */}
          <EntityAppointments scope="opportunity" entityId={opportunity.id} />
        </div>
      </div>

      {/* Gespreksverslagen - full width below */}
      <div className="w-full">
        <OpportunityNotes opportunityId={opportunity.id} />
      </div>

      {/* Edit Modal */}
      <OpportunityModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        opportunity={opportunity}
        onSave={handleSave}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Opportunity verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je "{opportunity.name}" wilt verwijderen? 
              Dit verwijdert ook alle gespreksverslagen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
